import { computed, readonly, ref } from 'vue';

export type ThemePreference = 'light' | 'dark' | 'system';
const storageKey = 'integrado.theme';
const system = window.matchMedia('(prefers-color-scheme: dark)');
const isPreference = (value: unknown): value is ThemePreference => value === 'light' || value === 'dark' || value === 'system';
function readPreference(): ThemePreference {
  try { const value = localStorage.getItem(storageKey); return isPreference(value) ? value : 'system'; }
  catch { return 'system'; }
}
const preference = ref<ThemePreference>(readPreference());
const systemDark = ref(system.matches);
const resolved = computed(() => preference.value === 'system' ? (systemDark.value ? 'dark' : 'light') : preference.value);
function applyTheme() {
  document.documentElement.dataset.theme = resolved.value;
  document.documentElement.style.colorScheme = resolved.value;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', resolved.value === 'dark' ? '#074b78' : '#0065a9');
}
function setPreference(value: ThemePreference) {
  if (!isPreference(value)) return;
  preference.value = value;
  try { localStorage.setItem(storageKey, value); } catch { /* The current page still honors the choice when storage is unavailable. */ }
  applyTheme();
}
// One listener for the application lifetime; changing pages does not register more listeners.
system.addEventListener('change', event => { systemDark.value = event.matches; if (preference.value === 'system') applyTheme(); });
window.addEventListener('storage', event => {
  if (event.key === storageKey || event.key === null) { preference.value = readPreference(); applyTheme(); }
});
applyTheme();

export function useTheme() { return { preference: readonly(preference), resolved, setPreference }; }
